/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ApplicationView,
  GSuiteFolderType,
  LetterTemplateDto,
  LetterTemplateLevel,
  LetterTemplateSourceDto,
  LetterTemplateValidationResultDto,
} from '@gh/builder-shared-data';
import { EMPTY_ID, ID, WithUniqueId } from '@gh/common-shared-data';
import { valueComparator } from '@gh/core-util';
import { isNil, pick } from 'lodash';

export type LetterTemplate = WithUniqueId<LetterTemplateDto>;
export type LetterTemplateValidationResult = LetterTemplateValidationResultDto;

export const getLetterTemplateId = (template: LetterTemplate) => template.uniqueId;
export const letterTemplateComparator = valueComparator('fileName');

export const isCorporateExist = (template: LetterTemplate) => !isNil(template.corporateFileId);
export const isMasterExist = (template: LetterTemplate) => !isNil(template.masterFileId);
export const isOfficeExist = (template: LetterTemplate) => !isNil(template.officeFileId);
export const isDraftExist = (template: LetterTemplate) => !isNil(template.draftFileId);

export const isCorporateUsed = (template: LetterTemplate) =>
  isCorporateExist(template) && !isMasterExist(template) && !isOfficeExist(template);
export const isMasterUsed = (template: LetterTemplate) => isMasterExist(template) && !isOfficeExist(template);
export const isOfficeUsed = (template: LetterTemplate) => isOfficeExist(template);

export const isTemplateUsed = (template: LetterTemplate) =>
  isCorporateExist(template) || isMasterExist(template) || isOfficeExist(template);

export const getGSuiteFolderTypeForView = (view: ApplicationView): GSuiteFolderType => {
  switch (view) {
    case ApplicationView.Corporate:
      return GSuiteFolderType.CorporateTemplates;
    case ApplicationView.Master:
      return GSuiteFolderType.MasterZoneTemplates;
    default:
      return GSuiteFolderType.OfficeTemplates;
  }
};

export const letterTemplateToSourceDto = (template: LetterTemplate, sourceDocId: ID) => ({
// @ts-expect-error Legacy typing suppression
  id: sourceDocId,
  ...<LetterTemplateSourceDto>pick(template, ['fileName', 'fileExtension', 'type']),
});

export const letterTemplateToCopyDto = (template: LetterTemplate, sourceDocId: ID, destination?: GSuiteFolderType) => ({
  template: letterTemplateToSourceDto(template, sourceDocId),
  direction: destination,
  etag: template.etag,
});

export const getLetterTemplateLevelId = (template: LetterTemplate,
                                         level: LetterTemplateLevel,
                                         isDraft: boolean): ID => {
  if (isDraft) {
    return template.draftFileId!;
  }
  switch (level) {
    case LetterTemplateLevel.Corporate:
      return template.corporateFileId!;
    case LetterTemplateLevel.Master:
      return template.masterFileId!;
    case LetterTemplateLevel.Office:
      return template.officeFileId!;
    default:
      return EMPTY_ID;
  }
};
